import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Loader2, BookOpen, Pencil, Check, X, Plus, Trash2, CalendarHeart,
  IndianRupee, Ban, Clock, HelpCircle, ListChecks, Send,
} from "lucide-react";
import { toast } from "sonner";

const COLOR_RING = {
  zinc: "border-l-zinc-400", sky: "border-l-sky-500", emerald: "border-l-emerald-500",
  amber: "border-l-amber-500", orange: "border-l-orange-500", red: "border-l-red-500",
  violet: "border-l-violet-500", pink: "border-l-pink-500", teal: "border-l-teal-500", blue: "border-l-blue-500",
};

// Renders multi-line text, preserving line breaks.
const Multiline = ({ text, className = "" }) =>
  (text || "").split("\n").map((line, i) => (
    <p key={i} className={`${className} ${line.trim() ? "" : "h-2"}`}>{line}</p>
  ));

export default function LeavePolicies() {
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [typeDrafts, setTypeDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/leave-policy");
      setData(d);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = () => {
    setDraft({ ...data.policy, faqs: (data.policy.faqs || []).map((f) => ({ ...f })) });
    const td = {};
    data.leave_types.forEach((t) => {
      td[t.key] = { description: t.description || "", entitlement: t.entitlement || "", rules: t.rules || "" };
    });
    setTypeDrafts(td);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/leave-policy", {
        overview: draft.overview, how_to_apply: draft.how_to_apply,
        general_rules: draft.general_rules, faqs: draft.faqs,
      });
      // Persist per-type educational fields only if they changed
      await Promise.all(data.leave_types.map((t) => {
        const td = typeDrafts[t.key];
        if (!td) return null;
        if (td.description !== (t.description || "") || td.entitlement !== (t.entitlement || "") || td.rules !== (t.rules || "")) {
          return api.put(`/leave-types/${t.key}`, td);
        }
        return null;
      }));
      toast.success("Leave policies updated");
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const paidTypes = useMemo(() => (data?.leave_types || []).filter((t) => t.is_paid), [data]);
  const unpaidTypes = useMemo(() => (data?.leave_types || []).filter((t) => !t.is_paid), [data]);

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-zinc-400" data-testid="leave-policies-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  if (!data) {
    return <div className="text-center py-24 text-zinc-400">Couldn't load leave policies.</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="leave-policies-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="label-overline">HR Policies</span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-zinc-400" strokeWidth={1.5} /> Leave Policies
          </h1>
          <p className="text-zinc-500 mt-2 max-w-2xl">Understand the leave you're entitled to, when each type applies, and how to request time off.</p>
        </div>
        {canEdit && !editing && (
          <Button variant="outline" onClick={startEdit} data-testid="edit-policies-btn"><Pencil className="w-4 h-4 mr-2" /> Edit</Button>
        )}
        {canEdit && editing && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditing(false)} disabled={saving} data-testid="cancel-policies-btn"><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-zinc-950 hover:bg-zinc-800" data-testid="save-policies-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Save
            </Button>
          </div>
        )}
      </div>

      {/* Overview */}
      <Section icon={BookOpen} title="Overview">
        {editing
          ? <Textarea rows={4} value={draft.overview} onChange={(e) => setDraft({ ...draft, overview: e.target.value })} data-testid="edit-overview" />
          : <div className="text-zinc-700 leading-relaxed space-y-2"><Multiline text={data.policy.overview} /></div>}
      </Section>

      {/* Leave type cards */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <CalendarHeart className="w-4 h-4 text-zinc-500" />
          <h2 className="font-display text-lg font-bold tracking-tight">Your leave types</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {[...paidTypes, ...unpaidTypes].map((t) => (
            <div key={t.key} className={`bg-white border border-zinc-200 border-l-4 ${COLOR_RING[t.color] || "border-l-zinc-400"} rounded-md p-4`} data-testid={`policy-type-${t.key}`}>
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold text-zinc-900">{t.label}</h3>
                <div className="flex items-center gap-1.5 shrink-0">
                  {t.half_day && <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">Half day</span>}
                  <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border inline-flex items-center gap-1 ${t.is_paid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                    {t.is_paid ? <IndianRupee className="w-2.5 h-2.5" /> : <Ban className="w-2.5 h-2.5" />}{t.is_paid ? "Paid" : "Unpaid"}
                  </span>
                </div>
              </div>
              {editing ? (
                <div className="space-y-2 mt-2">
                  <div>
                    <Label className="text-[11px] text-zinc-500">Entitlement</Label>
                    <Input value={typeDrafts[t.key]?.entitlement || ""} placeholder="e.g. 12 days / year" onChange={(e) => setTypeDrafts({ ...typeDrafts, [t.key]: { ...typeDrafts[t.key], entitlement: e.target.value } })} data-testid={`edit-entitlement-${t.key}`} className="h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-zinc-500">Description</Label>
                    <Textarea rows={2} value={typeDrafts[t.key]?.description || ""} onChange={(e) => setTypeDrafts({ ...typeDrafts, [t.key]: { ...typeDrafts[t.key], description: e.target.value } })} data-testid={`edit-desc-${t.key}`} className="text-sm" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-zinc-500">Rules / notes</Label>
                    <Textarea rows={2} value={typeDrafts[t.key]?.rules || ""} onChange={(e) => setTypeDrafts({ ...typeDrafts, [t.key]: { ...typeDrafts[t.key], rules: e.target.value } })} data-testid={`edit-rules-${t.key}`} className="text-sm" />
                  </div>
                </div>
              ) : (
                <>
                  {t.entitlement && (
                    <div className="mt-1.5 text-xs font-semibold text-zinc-600 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {t.entitlement}</div>
                  )}
                  {t.description && <p className="text-sm text-zinc-600 mt-1.5 leading-relaxed">{t.description}</p>}
                  {t.rules && (
                    <div className="mt-2 pt-2 border-t border-zinc-100 text-xs text-zinc-500 space-y-1"><Multiline text={t.rules} /></div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* How to apply */}
      <Section icon={Send} title="How to apply">
        {editing
          ? <Textarea rows={3} value={draft.how_to_apply} onChange={(e) => setDraft({ ...draft, how_to_apply: e.target.value })} data-testid="edit-how-to-apply" />
          : <div className="text-zinc-700 leading-relaxed space-y-2"><Multiline text={data.policy.how_to_apply} /></div>}
      </Section>

      {/* General rules */}
      <Section icon={ListChecks} title="General rules">
        {editing
          ? <Textarea rows={5} value={draft.general_rules} onChange={(e) => setDraft({ ...draft, general_rules: e.target.value })} data-testid="edit-general-rules" />
          : <div className="text-zinc-700 leading-relaxed space-y-1"><Multiline text={data.policy.general_rules} /></div>}
      </Section>

      {/* FAQs */}
      <Section icon={HelpCircle} title="FAQs">
        {editing ? (
          <div className="space-y-3">
            {(draft.faqs || []).map((f, i) => (
              <div key={i} className="border border-zinc-200 rounded-md p-3 space-y-2 relative" data-testid={`edit-faq-${i}`}>
                <Input value={f.q} placeholder="Question" onChange={(e) => { const faqs = [...draft.faqs]; faqs[i] = { ...faqs[i], q: e.target.value }; setDraft({ ...draft, faqs }); }} className="h-8 text-sm font-medium" data-testid={`edit-faq-q-${i}`} />
                <Textarea rows={2} value={f.a} placeholder="Answer" onChange={(e) => { const faqs = [...draft.faqs]; faqs[i] = { ...faqs[i], a: e.target.value }; setDraft({ ...draft, faqs }); }} className="text-sm" data-testid={`edit-faq-a-${i}`} />
                <button onClick={() => setDraft({ ...draft, faqs: draft.faqs.filter((_, j) => j !== i) })} className="absolute top-2 right-2 text-zinc-400 hover:text-red-600" data-testid={`del-faq-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setDraft({ ...draft, faqs: [...(draft.faqs || []), { q: "", a: "" }] })} data-testid="add-faq-btn"><Plus className="w-4 h-4 mr-1" /> Add FAQ</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {(data.policy.faqs || []).length === 0 && <p className="text-sm text-zinc-400">No FAQs yet.</p>}
            {(data.policy.faqs || []).map((f, i) => (
              <div key={i} className="border-b border-zinc-100 pb-3 last:border-0" data-testid={`faq-${i}`}>
                <div className="font-medium text-zinc-800 text-sm">{f.q}</div>
                <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        )}
      </Section>

      {data.updated_at && !editing && (
        <p className="text-xs text-zinc-400 text-center pt-2">Last updated {new Date(data.updated_at).toLocaleDateString()}</p>
      )}
    </div>
  );
}

const Section = ({ icon: Icon, title, children }) => (
  <section className="bg-white border border-zinc-200 rounded-md p-5">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
      <h2 className="font-display text-lg font-bold tracking-tight">{title}</h2>
    </div>
    {children}
  </section>
);
